import type { AxiosInstance } from 'axios';

export interface RedemptionBalanceBreakdown {
  spendableQixCredits: number;
  pendingMaturityQixCredits: number;
  redeemableQixCredits: number;
  nonRedeemableSpendableQixCredits: number;
  pendingPayoutQixCredits: number;
  lifetimeEarned: number;
  lifetimeDeposited: number;
  lifetimeRedeemed: number;
  holdDays: number;
  minDisbursement: number;
  maxDisbursement: number;
}

export interface RedemptionVerification {
  isEligible: boolean;
  isIdVerified: boolean;
  isPhoneVerified: boolean;
  isAddressVerified: boolean;
  isNameBoundToVerifiedIdentity: boolean;
  reason?: string | null;
}

export interface RedemptionTeamAliasSummary {
  aliasId: string;
  displayName?: string | null;
  aliasUsername: string;
  isOwner: boolean;
  canWithdraw: boolean;
  isBoundTarget: boolean;
}

export interface RedemptionStatus {
  enrollmentStatus: string;
  isAuthorizedForRedemption: boolean;
  earningsThresholdMet: boolean;
  minQualificationEarned: number;
  lifetimeEarned: number;
  verification: RedemptionVerification;
  missingRequirements: string[];
  isFrozen: boolean;
  freezeReason?: string | null;
  balance: RedemptionBalanceBreakdown;
  enrollmentFeeCents: number;
  isUsResidentOrBusiness?: boolean;
  isBusinessAccount?: boolean;
  nameBindingMet?: boolean;
  nameBindingMode?: 'none' | 'personal' | 'business_team_alias';
  boundTeamDisplayName?: string | null;
  eligibleTeamAlias?: RedemptionTeamAliasSummary | null;
}

export interface DisbursementHistoryItem {
  disbursementId: string;
  qixCreditsRedeemed: number;
  amountUsd: number;
  netDisbursed: number;
  status: string;
  method: string;
  requestedDate: string;
  completedDate?: string | null;
}

export interface DisbursementResult {
  success: boolean;
  message: string;
  disbursementId?: string;
  qixCreditsRedeemed?: number;
  amountUsd?: number;
  netDisbursed?: number;
  status?: string;
}

export interface RedemptionConnectStatus {
  connectPayoutsEnabled: boolean;
  requireConnectSetup: boolean;
  devSimulationAvailable: boolean;
  stripeConfigured: boolean;
  usedSimulation: boolean;
  hasConnectAccount: boolean;
  connectAccountId?: string | null;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  tinStatus: string;
  isReadyForPayout: boolean;
  disabledReason?: string | null;
  simulationMode?: string | null;
  payoutBlockers: string[];
}

export interface RedemptionConnectOnboardResult {
  success: boolean;
  usedLiveStripe?: boolean;
  simulationAvailable?: boolean;
  onboardingUrl?: string;
  errorMessage?: string;
}

export interface RedemptionConnectSimulateResult {
  success: boolean;
  outcome: string;
  message: string;
  status?: RedemptionConnectStatus;
}

export function createRedemptionService(api: Pick<AxiosInstance, 'get' | 'post'>) {
  return {
    async getStatus(): Promise<RedemptionStatus> {
      const { data } = await api.get<RedemptionStatus>('/api/redemption/status');
      return data;
    },

    async getBalanceBreakdown(): Promise<RedemptionBalanceBreakdown> {
      const { data } = await api.get<RedemptionBalanceBreakdown>('/api/redemption/balance-breakdown');
      return data;
    },

    async enroll(devMode = import.meta.env.DEV): Promise<{ success: boolean; message: string; status: RedemptionStatus }> {
      const { data } = await api.post('/api/redemption/enroll', { devMode });
      return data;
    },

    async requestDisbursement(qixCreditsAmount: number): Promise<DisbursementResult> {
      const { data } = await api.post<DisbursementResult>('/api/redemption/disbursement-request', {
        qixCreditsAmount,
      });
      return data;
    },

    async getDisbursementHistory(): Promise<DisbursementHistoryItem[]> {
      const { data } = await api.get<DisbursementHistoryItem[]>('/api/redemption/disbursements/history');
      return data;
    },

    async getLegalAcceptances(): Promise<{
      required: Array<{ documentType: string; documentVersion: string; label: string; accepted: boolean }>;
      accepted: Array<{ documentType: string; documentVersion: string; acceptedAt: string }>;
    }> {
      const { data } = await api.get('/api/redemption/legal-acceptances');
      return data;
    },

    async recordLegalAcceptance(documentType: string, documentVersion: string): Promise<void> {
      await api.post('/api/redemption/legal-acceptances', { documentType, documentVersion });
    },

    async getConnectStatus(): Promise<RedemptionConnectStatus> {
      const { data } = await api.get<RedemptionConnectStatus>('/api/redemption/connect/status');
      return data;
    },

    async startConnectOnboarding(returnUrl: string, refreshUrl: string): Promise<RedemptionConnectOnboardResult> {
      try {
        const { data } = await api.post<RedemptionConnectOnboardResult>('/api/redemption/connect/onboard', {
          returnUrl,
          refreshUrl,
        });
        return data;
      } catch (e: unknown) {
        const err = e as { response?: { data?: { error?: string; simulationAvailable?: boolean } } };
        return {
          success: false,
          simulationAvailable: err.response?.data?.simulationAvailable,
          errorMessage: err.response?.data?.error ?? 'Connect onboarding failed',
        };
      }
    },

    async simulateConnect(outcome: 'approved' | 'rejected' | 'reset'): Promise<RedemptionConnectSimulateResult> {
      const { data } = await api.post<RedemptionConnectSimulateResult>('/api/redemption/connect/dev/simulate', {
        outcome,
      });
      return data;
    },
  };
}
